import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useFeature } from '../../../context/AuthContext';
import useAdminAccess from '../../../context/useAdminAccess';
import { API_BASE_URL } from '../../../config';
import { toastSuccess, toastError, toastInfo } from '../../../lib/toast';
import OrgChartCompaniesPanel from './OrgChartCompaniesPanel';
import OrgChartTopOrderPanel from './OrgChartTopOrderPanel';
import {
  fetchDirectoryCompanySetting,
  saveDirectoryCompanySetting,
} from '../../orgChart/orgChartApi';
import OrgChartSyncPanel from './OrgChartSyncPanel';
import OrgDataQualityPanel from '../../orgChart/OrgDataQualityPanel';
import './odooSync.css';
import { formatDateTime } from '../../../lib/formatDate';

export default function OdooSyncTab() {
  // The org chart sub-tab is gated on the same flag as the user-facing page and
  // the server routes. Without this the tab still rendered with the flag off, and
  // its panels fetched endpoints that requireFeature('org_chart') rejects, so an
  // admin saw "Could not load the org chart sync status" for a feature that was
  // deliberately switched off. Flags are fail-open here, matching useFeature
  // everywhere else: a missing or errored flag leaves the tab visible.
  const orgChartEnabled = useFeature('org_chart');
  const { can, canAny } = useAdminAccess();
  const canSyncView = can('sync.view');
  const showOrgChartTab = orgChartEnabled && canAny(['org_chart.refresh', 'org_chart.settings', 'org_chart.view_all']);
  const showConfigTab = canSyncView || can('directory.settings');
  const visibleTabs = [showConfigTab && 'config', canSyncView && 'activity', showOrgChartTab && 'orgchart'].filter(Boolean);
  const [mapping, setMapping] = useState(null);
  const [events, setEvents] = useState(null);
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const [activeTab, setActiveTab] = useState(() => visibleTabs[0] || 'config'); // 'config' | 'activity' | 'orgchart'
  const [notMirroredOpen, setNotMirroredOpen] = useState(false);
  const [orgChartWarnings, setOrgChartWarnings] = useState([]);
  const [orgChartWarningsOutOfScope, setOrgChartWarningsOutOfScope] = useState({});

  // The selected sub-tab can become unavailable (flag switched off, or the
  // user lacks it), so fall back to the first visible one.
  // Keyed on a string, not the array: `visibleTabs` is a new array every render.
  const visibleKey = visibleTabs.join(',');
  useEffect(() => {
    const tabs = visibleKey ? visibleKey.split(',') : [];
    if (!tabs.includes(activeTab) && tabs[0]) setActiveTab(tabs[0]);
  }, [visibleKey, activeTab]);

  const mountedRef = useRef(true);
  const pollRef = useRef(null);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const refreshAudit = useCallback(async () => {
    const [m, e] = await Promise.all([
      axios.get(`${API_BASE_URL}/api/admin/odoo-sync/mapping`, { withCredentials: true }),
      axios.get(`${API_BASE_URL}/api/admin/odoo-sync/events`, { withCredentials: true }),
    ]);
    if (!mountedRef.current) return;
    setMapping(m.data.mapping || []);
    setEvents(e.data.events || []);
  }, []);

  const pollUntilDone = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const s = await axios.get(`${API_BASE_URL}/api/admin/odoo-sync/status`, { withCredentials: true });
        if (!mountedRef.current) return;
        setStatus(s.data);
        if (!s.data.running) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setRunning(false);
          const sum = s.data.last_summary;
          if (sum && sum.reassigned != null) {
            toastSuccess(`Sync complete · ${sum.added} added · ${sum.reassigned} moved · ${sum.origin_changes} origin updates.`);
          } else if (sum && sum.skipped) {
            toastInfo('Sync skipped — Odoo not configured.');
          } else {
            toastSuccess('Sync complete.');
          }
          await refreshAudit();
        }
      } catch {
        if (!mountedRef.current) return;
        clearInterval(pollRef.current);
        pollRef.current = null;
        setRunning(false);
        toastError('Lost track of the sync — refresh to see the latest state.');
      }
    }, 2000);
  }, [refreshAudit]);

  const load = useCallback(async () => {
    if (!canSyncView) return;
    try {
      const s = await axios.get(`${API_BASE_URL}/api/admin/odoo-sync/status`, { withCredentials: true });
      if (!mountedRef.current) return;
      setStatus(s.data);
      await refreshAudit();
      if (s.data.running) {
        setRunning(true);
        toastInfo('A sync is already running — watching progress…');
        pollUntilDone();
      }
    } catch {
      if (mountedRef.current) toastError('Could not load sync data.');
    }
  }, [canSyncView, pollUntilDone, refreshAudit]);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API_BASE_URL}/api/admin/odoo-sync/run`, {}, { withCredentials: true });
      if (!mountedRef.current) return;
      toastInfo('Odoo sync started.');
      if (r.status === 202 || r.data?.started) {
        pollUntilDone();
      } else {
        setRunning(false);
        await load();
      }
    } catch (err) {
      if (!mountedRef.current) return;
      if (err.response?.status === 409) {
        toastInfo('A sync is already running — watching progress…');
        pollUntilDone();
      } else {
        toastError('Sync failed to start.');
        setRunning(false);
      }
    }
  };


  return (
    <div className="osync">
      <header className="osync-header">
        <div>
          <h2>Odoo department sync</h2>
          <p className="osync-sub">
            Odoo HR is the source of truth. Department moves are applied automatically; the history below is read-only.
          </p>
        </div>
        <div className="osync-controls">
          {canSyncView && (
            <span className={`osync-pill ${status?.odoo_reachable ? 'ok' : 'bad'}`}>
              {status?.odoo_configured ? (status?.odoo_reachable ? 'Odoo connected' : 'Odoo unreachable') : 'Odoo not configured'}
            </span>
          )}
          {can('sync.run') && (
            <button type="button" className="osync-btn osync-btn--run" onClick={runNow} disabled={running}>
              {running ? 'Syncing…' : 'Sync now'}
            </button>
          )}
        </div>
      </header>

      {status?.last_run_at && (
        <p className="osync-laststamp">
          Last run {formatDateTime(status.last_run_at)}
          {status.last_summary && status.last_summary.reassigned != null &&
            ` · ${status.last_summary.added} added · ${status.last_summary.reassigned} moved`}
        </p>
      )}

      {/* ── Sub-tabs ──────────────────────────────── */}
      <nav className="osync-tabs" role="tablist" aria-label="Sync views">
        {showConfigTab && (
          <button type="button" role="tab" aria-selected={activeTab === 'config'}
            className={`osync-tab ${activeTab === 'config' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('config')}>
            Department configuration
          </button>
        )}
        {canSyncView && (
          <button type="button" role="tab" aria-selected={activeTab === 'activity'}
            className={`osync-tab ${activeTab === 'activity' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('activity')}>
            Activity log
            {events && events.length > 0 && <span className="osync-tab__badge">{events.length}</span>}
          </button>
        )}
        {showOrgChartTab && (
          <button type="button" role="tab" aria-selected={activeTab === 'orgchart'}
            className={`osync-tab ${activeTab === 'orgchart' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('orgchart')}>
            Org chart
          </button>
        )}
      </nav>

      {activeTab === 'config' && (() => {
        const mirrored = (mapping || []).filter((m) => m.nexus_department_id != null);
        const notMirrored = (mapping || []).filter((m) => m.nexus_department_id == null);
        const totalMembers = mirrored.reduce((sum, m) => sum + (m.member_count || 0), 0);
        return (
      <>
      {canSyncView && (
      <>
      {/* ── Stats strip ───────────────────────────── */}
      <section className="osync-stats" aria-label="Sync statistics">
        <div className="osync-stat">
          <span className="osync-stat__num">{mapping ? mirrored.length : '—'}</span>
          <span className="osync-stat__label">Mirrored departments</span>
        </div>
        <div className="osync-stat">
          <span className="osync-stat__num">{mapping ? mirrored.length : '—'}</span>
          <span className="osync-stat__label">Auto 1:1 links</span>
        </div>
        <div className="osync-stat">
          <span className="osync-stat__num">{mapping ? notMirrored.length : '—'}</span>
          <span className="osync-stat__label">Not mirrored</span>
        </div>
        <div className="osync-stat">
          <span className="osync-stat__num">{mapping ? totalMembers : '—'}</span>
          <span className="osync-stat__label">Members synced</span>
        </div>
      </section>

      {/* ── Department mapping (custom + effective, merged) ── */}
      <section className="osync-card">
        <div className="osync-card__head">
          <div>
            <h3>Department mapping</h3>
            <p className="osync-card__desc">
              The resolved Odoo → Nexus link for every mirrored department. Odoo is the source of truth:
              each Odoo department mirrors onto the Nexus department that carries its id.
            </p>
          </div>
          {mapping && <span className="osync-count">{mirrored.length} mirrored</span>}
        </div>



        {mapping === null && <div className="osync-loading">Loading…</div>}
        {mapping && mirrored.length === 0 && <div className="osync-empty">No mirrored departments yet.</div>}
        {mapping && mirrored.length > 0 && (
          <table className="admin-table osync-table" aria-label="Department mapping">
            <thead>
              <tr><th>Odoo department</th><th>Nexus department</th><th>Members</th></tr>
            </thead>
            <tbody>
              {mirrored.map((m) => (
                <tr key={`${m.odoo_department_id}-${m.nexus_department_id}`}>
                  <td>{m.odoo_department_name || m.nexus_department_name || `#${m.odoo_department_id}`}</td>
                  <td>{m.nexus_department_name}</td>
                  <td>{m.member_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* ── Not-mirrored Odoo depts (collapsible) ─── */}
      {mapping && notMirrored.length > 0 && (
        <section className="osync-card osync-collapse">
          <button type="button" className="osync-collapse__head" aria-expanded={notMirroredOpen}
            onClick={() => setNotMirroredOpen((o) => !o)}>
            <span className="osync-collapse__chev" aria-hidden="true">{notMirroredOpen ? '▾' : '▸'}</span>
            <span className="osync-collapse__title">Not mirrored in Nexus</span>
            <span className="osync-count">{notMirrored.length}</span>
          </button>
          {notMirroredOpen && (
            <div className="osync-collapse__body">
              <p className="osync-card__desc">Odoo departments with no Nexus department (typically empty or unused). Map one above to bring it in.</p>
              <ul className="osync-chiplist">
                {notMirrored.map((m) => (
                  <li key={m.odoo_department_id} className="osync-chip">
                    {m.odoo_department_name || `#${m.odoo_department_id}`}
                    <span className="osync-chip__count">{m.member_count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}
      </>
      )}

      {/* Deliberately on the always-available config tab, NOT inside the
          org_chart-gated block below: the employee directory is visible to every
          employee and must stay configurable even when the org chart feature
          flag is off. */}
      {can('directory.settings') && (
        <OrgChartCompaniesPanel
          inputId="dir-companies-input"
          label="Employee directory companies"
          help="Which Odoo companies appear in the employee directory. Separate from the org chart scope, because the directory is visible to every employee."
          fetcher={fetchDirectoryCompanySetting}
          saver={saveDirectoryCompanySetting}
          savedMessage="Employee directory companies updated"
          saveErrorMessage="Could not save the employee directory companies."
        />
      )}

      </>
        );
      })()}

      {/* Gated as well as the button above, not instead of it: the panels are what
          actually call the flag-gated endpoints, and hiding only the button would
          still render them when the flag is switched off while this tab happens
          to be the active one. */}
      {activeTab === 'orgchart' && showOrgChartTab && (
        <>
          {can('org_chart.refresh') && (
            <OrgChartSyncPanel
              onWarnings={(w, outOfScope) => {
                if (!mountedRef.current) return;
                setOrgChartWarnings(w);
                setOrgChartWarningsOutOfScope(outOfScope);
              }}
            />
          )}
          {can('org_chart.settings') && (
            <>
              <OrgChartCompaniesPanel />
              {/* After the company panel: the orderable set is scoped to the
                  configured companies, so the scope is the thing to settle first. */}
              <OrgChartTopOrderPanel />
            </>
          )}
          {can('org_chart.view_all') && (
            <OrgDataQualityPanel warnings={orgChartWarnings} outOfScope={orgChartWarningsOutOfScope} />
          )}
        </>
      )}

      {activeTab === 'activity' && (
      /* ── Recent moves (audit) ──────────────────── */
      <section className="osync-card">
        <div className="osync-card__head">
          <div>
            <h3>Recent moves</h3>
            <p className="osync-card__desc">Department reassignments applied automatically by sync.</p>
          </div>
          {events && events.length > 0 && <span className="osync-count">{events.length} event{events.length === 1 ? '' : 's'}</span>}
        </div>
        {events === null && <div className="osync-loading">Loading…</div>}
        {events && events.length === 0 && <div className="osync-empty">No department moves recorded yet.</div>}
        {events && events.length > 0 && (
          <table className="admin-table osync-table" aria-label="Recent department moves">
            <thead>
              <tr><th>User</th><th>Move</th><th>Tools</th><th>Kind</th><th>When</th></tr>
            </thead>
            <tbody>
              {events.map((ev) => (
                <tr key={ev.id}>
                  <td>{ev.user_name}</td>
                  <td className="osync-route">
                    {ev.from_department_name || '—'} <span aria-hidden="true">→</span> <span className="osync-to">{ev.to_department_name || '—'}</span>
                  </td>
                  <td className="osync-delta">
                    <span className="pos">+{(ev.tools_added || []).length}</span>
                    {' / '}
                    <span className="neg">−{(ev.tools_removed || []).length}</span>
                  </td>
                  <td><span className={`osync-tag osync-tag--${ev.kind}`}>{(ev.kind || '').replace('_', ' ')}</span></td>
                  <td className="osync-when">{formatDateTime(ev.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      )}
    </div>
  );
}
